import { useEffect, useRef, useState } from 'react';
import { Avatar, Button, Card, Dialog, Empty, Input, Space, SpinLoading, Tabs, Toast } from 'antd-mobile';
import { LinkOutline, CloseOutline, CheckOutline, ScanCodeOutline, GiftOutline } from 'antd-mobile-icons';
import { fetchCoupons, fetchInviteLink, getSpecialCodeCoupon, getMemberQRCode, getExpiredCoupons, getWorkerQRCode, getNationalDay2026Prop } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getShopId } from '../utils/shopId';
import {
  clearRegistrationRefreshPending,
  isRegistrationRefreshPending,
} from '../utils/registrationSession';
import UserHeader from '../components/UserHeader';
import homeTopImg from '../assets/Ui_Home_Top.png';
import linkBgImg from '../assets/Ui_Home_Link_Bg.png';
import './HomePage.css';

export default function HomePage() {
  const { user, logout, refreshSession } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeKey, setActiveKey] = useState('home');
  const [coupons, setCoupons] = useState([]);
  const [nationalDayProp, setNationalDayProp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [registrationRefreshReady, setRegistrationRefreshReady] = useState(
    () => !isRegistrationRefreshPending(),
  );
  const specialCodeRef = useRef('');
  const couponList = Array.isArray(coupons) ? coupons : [];
  const hasCoupons = couponList.length > 0;
  const hasHomeRewards = hasCoupons || !!nationalDayProp;
  const inviteLink = user?.id ? `https://m.taogo.shop/register?referId=${user.id}` : '';

  useEffect(() => {
    if (registrationRefreshReady) {
      return undefined;
    }
    let cancelled = false;
    (async () => {
      await refreshSession();
      if (!cancelled) {
        setRegistrationRefreshReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshSession, registrationRefreshReady]);

  useEffect(() => {
    let cancelled = false;

    const fetchCouponList = async () => {
      if (!registrationRefreshReady) {
        if (!cancelled) {
          setLoading(true);
        }
        return;
      }

      if (!user?.id) {
        if (!cancelled) {
          setCoupons([]);
          setLoading(false);
        }
        return;
      }

      if (!cancelled) {
        setLoading(true);
      }

      try {
        let payload = await fetchCoupons(user.id);
        if (isRegistrationRefreshPending() && (!Array.isArray(payload) || payload.length === 0)) {
          await new Promise((resolve) => setTimeout(resolve, 500));
          payload = await fetchCoupons(user.id);
        }
        if (!cancelled) {
          setCoupons(Array.isArray(payload) ? payload : []);
          clearRegistrationRefreshPending();
        }
      } catch (error) {
        if (!cancelled) {
          setCoupons([]);
          Toast.show({ icon: 'fail', content: error.message || 'Failed to load coupons' });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchCouponList();

    return () => {
      cancelled = true;
    };
  }, [registrationRefreshReady, user?.id]);

  useEffect(() => {
    let cancelled = false;
    if (!registrationRefreshReady || !user?.id) {
      setNationalDayProp(null);
      return undefined;
    }
    (async () => {
      try {
        const prop = await getNationalDay2026Prop(user.id);
        if (!cancelled) setNationalDayProp(prop || null);
      } catch (_) {
        if (!cancelled) setNationalDayProp(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [registrationRefreshReady, user?.id]);

  const handleShowNationalDayProp = () => {
    if (!nationalDayProp?.code) return;
    const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=320x320&data=${encodeURIComponent(nationalDayProp.code)}`;
    const dialog = Dialog.show({
      title: 'Malaysia National Day Token',
      content: (
        <div style={{ textAlign: 'center', padding: '12px 0' }}>
          <div style={{ fontSize: 52, marginBottom: 8 }}>🇲🇾</div>
          <img src={qrCodeUrl} alt="Malaysia National Day Token QR Code" style={{ width: 260, height: 260, maxWidth: '100%' }} />
          <p style={{ marginTop: 12, fontSize: 14, color: '#555' }}>
            Show this QR code to store staff for redemption.
          </p>
          <p style={{ fontSize: 12, color: '#999' }}>Valid until 31 August 2026</p>
        </div>
      ),
      closeOnMaskClick: true,
      actions: [{ key: 'close', text: 'Close', onClick: () => dialog.close() }],
    });
  };

  const FLASH_CELEBRATION_KEY = 'taogo-flash-coupon-20260520-celebration';

  useEffect(() => {
    if (!user?.id || loading) {
      return;
    }
    try {
      if (sessionStorage.getItem(FLASH_CELEBRATION_KEY) === '1') {
        sessionStorage.removeItem(FLASH_CELEBRATION_KEY);
        (async () => {
          try {
            const payload = await fetchCoupons(user.id);
            setCoupons(Array.isArray(payload) ? payload : []);
          } catch (_) {
            /* ignore */
          }
        })();
        const dialog = Dialog.show({
          title: (
            <span style={{ textAlign: 'center', display: 'block' }}>
              🎉 Congratulations!
            </span>
          ),
          content: (
            <div style={{ textAlign: 'center', padding: '8px 0' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  margin: '0 0 8px',
                }}
              >
                <GiftOutline style={{ fontSize: 48, color: '#824BFF' }} />
              </div>
              <p>
                You&apos;ve received <strong>6 TaoGo Flash coupons</strong> (RM465 total discount, valid for{' '}
                <strong>two months</strong> from today).
              </p>
              <p style={{ color: '#666', fontSize: 14 }}>Check your coupon list below.</p>
            </div>
          ),
          closeOnMaskClick: true,
          actions: [
            {
              key: 'ok',
              text: 'Awesome!',
              onClick: () => {
                dialog.close();
              },
            },
          ],
        });
      }
    } catch (_) {
      /* ignore */
    }
  }, [user?.id, loading]);

  // Capture shopId from URL parameter
  useEffect(() => {
    const capturedShopId = getShopId(searchParams);
    // shopId is stored in localStorage by getShopId utility
  }, [searchParams]);

  const handleCopyLink = async () => {
    if (!inviteLink) {
      Toast.show({ content: 'Invite link is not available' });
      return;
    }

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(inviteLink);
        Toast.show({ icon: 'success', content: 'Invite link copied to clipboard!' });
      } else {
        Toast.show({ content: inviteLink });
      }
    } catch (error) {
      Toast.show({ icon: 'fail', content: 'Failed to copy link, please try again' });
    }
  };

  const handleInvite = async () => {
    if (!user?.id) {
      Toast.show({ content: 'Please sign in again to get QR code' });
      return;
    }

    const loading = Toast.show({
      icon: 'loading',
      content: 'Loading QR code...',
      duration: 0,
    });

    try {
      // 获取会员二维码（扫码落地 URL 与「复制邀请链接」一致：referId；后端 qrCodeLink 仅兜底）
      const response = await getMemberQRCode(user.id);
      const qrPayload = inviteLink || response.qrCodeLink;
      const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(qrPayload)}`;
      
      loading.close();

      // 检测是否为移动设备
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      
      if (isMobile) {
        // 移动端：显示二维码对话框，让用户长按保存
        const dialog = Dialog.show({
          title: 'Invite QR Code',
          content: (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <img 
                src={qrCodeUrl}
                alt="Invite QR Code"
                style={{ width: '250px', height: '250px', margin: '0 auto' }}
              />
              <p style={{ marginTop: '16px', fontSize: '14px', color: '#666' }}>
                Member Code: {response.memberCode}
              </p>
              <p style={{ fontSize: '12px', color: '#999', marginTop: '8px' }}>
                Long press the QR code to save or share
              </p>
            </div>
          ),
          closeOnMaskClick: true,
          actions: [
            {
              key: 'close',
              text: 'Close',
              onClick: () => {
                dialog.close();
              },
            },
          ],
        });
      } else {
        // 桌面端：尝试复制到剪贴板
        try {
          if (navigator.clipboard && window.ClipboardItem) {
            const qrResponse = await fetch(qrCodeUrl);
            const blob = await qrResponse.blob();
            const item = new window.ClipboardItem({ [blob.type]: blob });
            await navigator.clipboard.write([item]);
            Toast.show({ icon: 'success', content: 'QR code copied to clipboard!' });
          } else {
            Toast.show({ content: 'Please use MemberQR button to view QR code' });
          }
        } catch (copyError) {
          console.error('Copy failed:', copyError);
          Toast.show({ icon: 'fail', content: 'Failed to copy, please try again' });
        }
      }
    } catch (error) {
      loading.close();
      Toast.show({ icon: 'fail', content: error.message || 'Failed to load QR code' });
    }
  };

  const handleLogout = () => {
    logout();
    Toast.show({ icon: 'success', content: 'Signed out' });
    navigate('/login', { replace: true });
  };

  const handleViewExpiredCoupons = async () => {
    if (!user?.id) {
      Toast.show({ content: 'Please sign in to view expired coupons' });
      return;
    }

    const loading = Toast.show({
      icon: 'loading',
      content: 'Loading...',
      duration: 0,
    });

    try {
      const expiredCoupons = await getExpiredCoupons(user.id);
      loading.close();

      if (!expiredCoupons || expiredCoupons.length === 0) {
        Toast.show({
          icon: 'success',
          content: 'No expired coupons',
        });
        return;
      }

      // 显示过期优惠券列表
      const dialog = Dialog.show({
        title: 'Expired Coupons',
        content: (
          <div style={{ maxHeight: '400px', overflow: 'auto' }}>
            {expiredCoupons.map((coupon, index) => {
              const discountAmount = coupon.discountAmount ?? coupon.amount ?? coupon.value;
              const minimumAmount = coupon.minimumAmount ?? coupon.minAmount ?? coupon.thresholdAmount;
              const formattedAmount =
                discountAmount === null || discountAmount === undefined
                  ? '--'
                  : typeof discountAmount === 'number'
                    ? discountAmount.toFixed(discountAmount % 1 === 0 ? 0 : 2)
                    : discountAmount;
              
              return (
                <div
                  key={coupon.id}
                  style={{
                    padding: '12px',
                    marginBottom: '8px',
                    background: '#f5f5f5',
                    borderRadius: '8px',
                    borderLeft: '3px solid #999',
                  }}
                >
                  <div style={{ fontWeight: 600, fontSize: '14px', color: '#333', marginBottom: '4px' }}>
                    {coupon.title || 'Special Offer'}
                  </div>
                  <div style={{ fontSize: '13px', color: '#666', marginBottom: '4px' }}>
                    Discount: RM {formattedAmount}
                  </div>
                  {minimumAmount !== null && minimumAmount !== undefined && Number(minimumAmount) > 0 && (
                    <div style={{ fontSize: '12px', color: '#999' }}>
                      Minimum spend: RM {typeof minimumAmount === 'number'
                        ? minimumAmount.toFixed(minimumAmount % 1 === 0 ? 0 : 2)
                        : minimumAmount}
                    </div>
                  )}
                  <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                    Expired: {coupon.expireDate || '--'}
                  </div>
                </div>
              );
            })}
          </div>
        ),
        closeOnMaskClick: true,
        actions: [
          {
            key: 'close',
            text: 'Close',
            onClick: () => {
              dialog.close();
            },
          },
        ],
      });
    } catch (error) {
      loading.close();
      Toast.show({
        icon: 'fail',
        content: error.message || 'Failed to load expired coupons',
      });
    }
  };

  const refreshCoupons = async () => {
    if (!user?.id) {
      return;
    }
    try {
      setLoading(true);
      const payload = await fetchCoupons(user.id);
      setCoupons(Array.isArray(payload) ? payload : []);
    } catch (error) {
      Toast.show({ icon: 'fail', content: error.message || 'Failed to refresh coupons' });
    } finally {
      setLoading(false);
    }
  };

  const handleGetSpecialCoupon = () => {
    if (!user?.id) {
      Toast.show({ content: 'Please sign in again' });
      return;
    }

    specialCodeRef.current = '';

    const dialog = Dialog.show({
      title: 'Get Special Coupon',
      content: (
        <div style={{ padding: '8px 0' }}>
          <Input
            placeholder="Input special code"
            defaultValue=""
            onChange={(value) => {
              specialCodeRef.current = value;
            }}
            onEnterPress={() => {
              dialog.close();
              submitSpecialCode();
            }}
          />
          <p style={{ marginTop: '8px', fontSize: '12px', color: '#999', textAlign: 'left' }}>
            Case insensitive
          </p>
        </div>
      ),
      actions: [
        {
          key: 'cancel',
          text: 'Cancel',
          onClick: () => {
            specialCodeRef.current = '';
            dialog.close();
          },
        },
        {
          key: 'submit',
          text: 'Submit',
          onClick: () => {
            dialog.close();
            submitSpecialCode();
          },
        },
      ],
    });
  };

  const submitSpecialCode = async () => {
    const code = specialCodeRef.current.trim();
    if (!code) {
      Toast.show({ content: 'Please enter a special code' });
      return;
    }

    if (!user?.id) {
      Toast.show({ content: 'Please sign in again' });
      return;
    }

    const toastHandler = Toast.show({ icon: 'loading', content: 'Processing...', duration: 0 });
    try {
      await getSpecialCodeCoupon(user.id, code);
      toastHandler.close();
      Toast.show({ icon: 'success', content: 'Special coupon received successfully!' });
      specialCodeRef.current = '';
      // Refresh coupon list
      await refreshCoupons();
    } catch (error) {
      toastHandler.close();
      Toast.show({ icon: 'fail', content: error.message || 'Failed to get special coupon' });
    }
  };

  const handleShowWorkerQRCode = async () => {
    if (!user?.id) {
      Toast.show({ content: 'Please sign in again to get worker QR code' });
      return;
    }

    const loading = Toast.show({
      icon: 'loading',
      content: 'Loading worker QR code...',
      duration: 0,
    });

    try {
      const response = await getWorkerQRCode(user.id);
      const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(response.qrCodeLink)}`;
      
      loading.close();

      // 显示员工二维码对话框
      const dialog = Dialog.show({
        title: 'Worker QR Code',
        content: (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <img 
              src={qrCodeUrl}
              alt="Worker QR Code"
              style={{ width: '250px', height: '250px', margin: '0 auto' }}
            />
            <p style={{ marginTop: '16px', fontSize: '14px', color: '#666' }}>
              Worker Code: {response.workerCode}
            </p>
            <p style={{ fontSize: '12px', color: '#999', marginTop: '8px' }}>
              Members can scan this QR code to bind you as their customer service
            </p>
          </div>
        ),
        closeOnMaskClick: true,
        actions: [
          {
            key: 'close',
            text: 'Close',
            onClick: () => {
              dialog.close();
            },
          },
        ],
      });
    } catch (error) {
      loading.close();
      Toast.show({
        icon: 'fail',
        content: error.message || 'Failed to load worker QR code',
      });
    }
  };

  return (
    <div className="home-page">
      <div className="home-top-image">
        <img src={homeTopImg} alt="Home Top" />
      </div>
      <div className="home-content">
        {activeKey === 'home' ? (
          <>
            <UserHeader user={user} />
            <div className="scroll-container">
              {loading ? (
                <div className="loading-state">
                  <SpinLoading color="primary" />
                  <span>Loading...</span>
                </div>
              ) : !hasHomeRewards ? (
                <Empty description="No coupons or event tokens available" />
              ) : (
                <Space direction="vertical" block size={16}>
                  {nationalDayProp && (
                    <Card className="national-day-prop-card" onClick={handleShowNationalDayProp}>
                      <div className="national-day-prop-flag">🇲🇾</div>
                      <div className="national-day-prop-content">
                        <div className="national-day-prop-title">Malaysia National Day Riddle Token</div>
                        <div className="national-day-prop-subtitle">Tap to show your redemption QR code</div>
                        <div className="national-day-prop-date">Activity: 26–31 August 2026</div>
                      </div>
                      <ScanCodeOutline className="national-day-prop-scan" />
                    </Card>
                  )}
                  {/* Coupons List */}
                  {hasCoupons && couponList.map((coupon, index) => {
                    const effectiveDate = coupon.effectiveDate || coupon.startDate;
                    const expireDate = coupon.expireDate || coupon.expireAt || coupon.expiry;
                    const discountAmount = coupon.discountAmount ?? coupon.amount ?? coupon.value;
                    const minimumAmount = coupon.minimumAmount ?? coupon.minAmount ?? coupon.thresholdAmount;
                    const formattedAmount =
                      discountAmount === null || discountAmount === undefined
                        ? '--'
                        : typeof discountAmount === 'number'
                          ? discountAmount.toFixed(discountAmount % 1 === 0 ? 0 : 2)
                          : discountAmount;
                    const isStoreOpeningCoupon = coupon.type === 'store_opening';
                    const promotionType = coupon.promotionChannelType;
                    const promotionHeadline = promotionType === 'influencer'
                      ? 'CREATOR EXCLUSIVE'
                      : promotionType === 'private_community'
                        ? 'COMMUNITY EXCLUSIVE'
                        : 'TAOGO 19 OPENING OFFER';
                    const hasSpecialPromotionVisual = isStoreOpeningCoupon
                      && (promotionType === 'influencer' || promotionType === 'private_community');
                    // Cycle through 4 colors: #824BFF, #FF4b64, #3AC55E, #F4792A
                    const colorIndex = index % 4;
                    const colorTier = `tier-color-${colorIndex}`;
                    return (
                      <Card key={coupon.id} className={`coupon-card ${hasSpecialPromotionVisual ? `store-opening-${promotionType}` : ''}`}>
                        <div className="coupon-card-inner">
                          <div className={`coupon-left ${colorTier}`}>
                            <div className="coupon-amount-badge">
                              <span className="coupon-currency">RM</span>
                              <span className="coupon-value">{formattedAmount}</span>
                            </div>
                            <span className="coupon-caption">{isStoreOpeningCoupon ? 'OFF' : 'Discount'}</span>
                          </div>
                          <div className="coupon-right">
                            {isStoreOpeningCoupon && (
                              <div className="promotion-headline">{promotionHeadline}</div>
                            )}
                            <div className="coupon-title">{isStoreOpeningCoupon ? 'RM10 OFF' : (coupon.title || 'Special Offer')}</div>
                            {minimumAmount !== null && minimumAmount !== undefined && Number(minimumAmount) > 0 && (
                              <div className="coupon-minimum">
                                {isStoreOpeningCoupon ? 'with minimum spend of RM' : 'Minimum spend: RM'}
                                {typeof minimumAmount === 'number'
                                  ? minimumAmount.toFixed(minimumAmount % 1 === 0 ? 0 : 2)
                                  : minimumAmount}
                              </div>
                            )}
                            {(effectiveDate || expireDate) && (
                              <div className="coupon-dates">
                                <span className="coupon-date-item">
                                  <strong>Effective:</strong> {effectiveDate || '--'}
                                </span>
                                {expireDate && (
                                  <span className="coupon-date-item">
                                    <strong>Expires:</strong> {expireDate || '--'}
                                  </span>
                                )}
                              </div>
                            )}
                            {isStoreOpeningCoupon && (
                              <div className="promotion-final-interpretation">
                                TaoGo reserves all rights of final interpretation.
                              </div>
                            )}
                          </div>
                        </div>
                      </Card>
                    );
                  })}
                </Space>
              )}
            </div>
          </>
        ) : (
          <>
            <UserHeader user={user} />
            <div className="scroll-container profile-scroll">
              <Space direction="vertical" block size={0}>
                <div className="invite-info-card">
                <div className="invite-link-bg">
                  <img src={linkBgImg} alt="Link Background" />
                  <span className="invite-link-bg-text">Invite Friends</span>
                </div>
                <div className="invite-link-container">
                  <div className="invite-link-display">
                    {inviteLink || 'Invite link will appear here after sign-in'}
                  </div>
                  <Button
                    size="small"
                    color="primary"
                    onClick={handleCopyLink}
                    className="invite-copy-button"
                  >
                    Copy
                  </Button>
                </div>
                <Button
                  block
                  color="primary"
                  onClick={handleInvite}
                  icon={<LinkOutline />}
                  className="invite-button"
                  style={{ marginTop: '12px' }}
                >
                  Invite QR
                </Button>
                
                <div className="invite-description">
                  <div className="invite-description-title">
                    Earn RM5 For Every Friend You Invite!
                  </div>
                  <div className="invite-description-subtitle">How it Works:</div>
                  <div className="invite-description-steps">
                    <div className="invite-step">1. Share your invitation link or code with a friend to sign up.</div>
                    <div className="invite-step">2. After they join, their first in-store purchase reaches RM28.</div>
                    <div className="invite-step">3. You'll instantly receive an RM5.</div>
                  </div>
                  <div className="invite-benefits">
                    <div className="invite-benefit-item">
                      <CheckOutline style={{ color: '#52c41a', fontSize: '16px', marginRight: '8px' }} />
                      <span>No minimum spend - Use it on anything.</span>
                    </div>
                    <div className="invite-benefit-item">
                      <CheckOutline style={{ color: '#52c41a', fontSize: '16px', marginRight: '8px' }} />
                      <span>Stackable - Save even more by using multiple vouchers together</span>
                    </div>
                  </div>
                </div>
              </div>
              <Space direction="vertical" block size={16}>
                <Button
                  block
                  color="primary"
                  onClick={handleGetSpecialCoupon}
                  className="special-coupon-button"
                >
                  Get Special Coupon
                </Button>
                {/* 如果是员工，显示员工二维码按钮 */}
                {user?.isWorker === 1 && (
                  <Button
                    block
                    color="primary"
                    onClick={handleShowWorkerQRCode}
                    icon={<ScanCodeOutline />}
                  >
                    Show Worker QR Code
                  </Button>
                )}
                <Button
                  block
                  color="default"
                  onClick={handleViewExpiredCoupons}
                >
                  View Expired Coupons
                </Button>
                <Button block color="danger" onClick={handleLogout} icon={<CloseOutline />}>
                  Sign Out
                </Button>
              </Space>
            </Space>
            </div>
          </>
        )}
      </div>
      <Tabs
        activeKey={activeKey}
        onChange={setActiveKey}
        className="home-tabs"
        tabBarPosition="bottom"
      >
        <Tabs.Tab key="home" title="Home">
        </Tabs.Tab>
        <Tabs.Tab key="profile" title="Profile">
        </Tabs.Tab>
      </Tabs>
    </div>
  );
}
